/**
 * Scroll reveal.
 *
 * Adds `is-revealed` to any `[data-bo-reveal]` element once it enters the
 * viewport. The movement itself lives in burnout.css, so reduced-motion is
 * handled by the stylesheet rather than branched on here.
 *
 * Elements are unobserved after revealing — this is a one-way entrance, not a
 * scroll-linked effect, so there is no reason to keep watching them.
 *
 * A single shared observer handles the whole page, and a MutationObserver
 * picks up anything added later (theme editor re-renders a section on every
 * setting change, and those nodes would otherwise stay invisible forever).
 */
const REVEAL_SELECTOR = '[data-bo-reveal]';

function revealImmediately(root = document) {
  root.querySelectorAll(REVEAL_SELECTOR).forEach((el) => el.classList.add('is-revealed'));
}

// No IntersectionObserver, or the visitor asked for less motion: show everything.
if (
  !('IntersectionObserver' in window) ||
  window.matchMedia('(prefers-reduced-motion: reduce)').matches
) {
  revealImmediately();
} else {
  // Only now do we let the stylesheet hide anything. Everything above this
  // point bails out with content visible.
  document.documentElement.classList.add('bo-reveal-on');

  // Failsafe. IntersectionObserver callbacks are tied to the rendering loop,
  // so a backgrounded tab, a restored bfcache page, or a browser that never
  // schedules a frame can leave elements hidden indefinitely. After a short
  // grace period, show whatever is still waiting — a missed animation is a
  // detail, invisible content is a broken page.
  setTimeout(() => revealImmediately(), 2500);

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        observer.unobserve(entry.target);
      });
    },
    // Fire slightly before the element is fully on screen, so the movement is
    // already settling by the time it is properly in view.
    { rootMargin: '0px 0px -8% 0px', threshold: 0.01 }
  );

  const observe = (root = document) => {
    root.querySelectorAll(REVEAL_SELECTOR).forEach((el) => {
      if (!el.classList.contains('is-revealed')) observer.observe(el);
    });
  };

  observe();

  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        if (node.matches?.(REVEAL_SELECTOR)) observer.observe(node);
        observe(node);
      });
    });
  }).observe(document.body, { childList: true, subtree: true });

  // In the theme editor nothing should ever sit hidden waiting for a scroll.
  if (window.Shopify?.designMode) {
    document.addEventListener('shopify:section:load', (event) => observe(event.target));
  }
}
